import React, { useCallback, useRef } from "react";
import { ArrayField, useFieldArray, useWatch } from "react-hook-form";
import { Question } from "../../graphql/types";
import useTranslations from "../../hooks/useTranslations";
import { FormStepProps } from "../../screens/ReviewFormScreen";
import {
  DeleteIcon,
  MenuIcon as TextLongIcon,
  PlusIcon,
  StarIcon,
} from "../icons";
import { 
  Button, 
  Checkbox, 
  Input 
} from "../wombat-ui";
import {
  Tooltip
} from "@chakra-ui/react";

export const SkillGoalOption: React.FC<
  Omit<
    FormStepProps,
    "onNext" | "formState" | "getValues" | "unregister" | "reset" | "errors"
  > & {
    fieldIndex: number;
    defaultValue: boolean;
    name: string;
  }
> = React.memo(
  ({ fieldIndex, register, control, setValue, defaultValue, name }) => {
    const t = useTranslations();
    const options: any = useWatch({
      control,
      name: `${name}.options`,
    });

    const handleOptionChecked = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked)
          options.forEach((o, innerIndex) => {
            if (o.isGoal && innerIndex !== fieldIndex)
              setValue(`${name}.options[${innerIndex}].isGoal`, false);
          });
        setValue(`${name}.options[${fieldIndex}].isGoal`, e.target.checked);
      },
      [options, fieldIndex, name]
    );

    return (
      <Checkbox
        label={t("reviews.setAsGoal") as string}
        srOnlyLabel
        checkedIcon={
          <Tooltip 
            label={t("reviews.unsetAsGoal") as string} 
            placement="left" 
            hasArrow
          >
            <div>
              <StarIcon
                className="w-5 h-5 cursor-pointer fill-current text-warning-500"
              />
            </div>
          </Tooltip>
        }
        uncheckedIcon={
          <Tooltip 
            label={t("reviews.setAsGoal") as string} 
            placement="left" 
            hasArrow
          >
            <div>
              <StarIcon
                className="w-5 h-5 cursor-pointer text-warning-300 hover:text-warning-500 hover:fill-current"
              />
            </div>
          </Tooltip>
        }
        id={`${name}.options[${fieldIndex}].isGoal`}
        name={`${name}.options[${fieldIndex}].isGoal`}
        defaultChecked={defaultValue}
        ref={register()}
        onChange={handleOptionChecked}
      />
    );
  }
);

export type QuestionFormInput = Omit<
  Question,
  "id" | "defaultValue" | "optionsSchema" | "defaultValueSchema" | "__typename"
>;

const QuestionWithOptionsForm: React.FC<
  Omit<FormStepProps, "onNext" | "formState" | "reset" | "trigger"> & {
    defaultValue: Partial<ArrayField<QuestionFormInput, "id">>;
    fixedOptions?: boolean;
    hidden?: boolean;
    name: string;
  }
> = React.memo(
  ({
    control,
    register,
    setValue,
    fixedOptions = false,
    name,
  }) => {
    const t = useTranslations();
    const { append, remove, fields } = useFieldArray<{
      value: string;
      label: string;
    }>({
      control,
      name: `${name}.options`,
    });
    const inputRef = useRef<HTMLInputElement>();

    return (
      <div className="grid grid-cols-12 gap-2 pt-2">
        {fields.map((field, fieldIndex) => {
          return (
            <React.Fragment key={field.id}>
              <div
                className="flex items-center w-full col-span-10 space-x-2 col-start-0"
                key={field.id}
              >
                <span className="w-20 text-xs text-primary-800">
                  {t("reviews.optionNumber", { number: fieldIndex + 1 })}
                </span>
                <div className="flex-grow">
                  <Input
                    srOnlyLabel
                    label={t("reviews.optionNumber", { number: fieldIndex + 1 }) as string}
                    name={`${name}.options[${fieldIndex}].label`}
                    defaultValue={field.label}
                    ref={register()}
                    readOnly={fixedOptions}
                    id={`${name}.options[${fieldIndex}].label`}
                  />
                  <input
                    type="hidden"
                    ref={register()}
                    defaultValue={`${fieldIndex}`}
                    name={`${name}.options[${fieldIndex}].value`}
                  />
                </div>
              </div>
              {!fixedOptions && (
                <Button
                  icon={DeleteIcon}
                  variant="ghost"
                  onClick={() => {
                    remove(fieldIndex);
                  }}
                />
              )}
              {fixedOptions && (
                <div className="flex items-center col-span-1 px-2">
                  <SkillGoalOption
                    register={register}
                    setValue={setValue}
                    control={control}
                    defaultValue={(field as any)?.isGoal}
                    name={name}
                    fieldIndex={fieldIndex}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
        {!fixedOptions && (
          <React.Fragment>
            <div className="flex items-center w-full col-span-10 space-x-2 col-start-0">
              <div className="flex-grow">
                <Input
                  srOnlyLabel
                  label={t("reviews.addOption") as string}
                  placeholder={t("reviews.addOption") as string}
                  id={`${name}.addOptionLabel`}
                  ref={inputRef}
                />
              </div>
            </div>
            <div className="flex-col items-center justify-center mt-0.5">
              <Button
                icon={PlusIcon}
                id={`${name}.addOption`}
                variant="subtle"
                onClick={() => {
                  if (inputRef?.current?.value) {
                    append({
                      value: fields.length.toString(),
                      label: inputRef.current.value,
                    });
                    inputRef.current.value = "";
                  }
                }}
              />
            </div>
          </React.Fragment>
        )}
      </div>
    );
  }
);

export default React.memo(QuestionWithOptionsForm);
